/**
 * 可驗證公平（Provably Fair）—— 瀏覽器端驗證。C01 §4.2。
 *
 * 這是**教學功能**：我們要讓玩家自己在瀏覽器裡按一顆按鈕，用 Web Crypto 重算雜湊，
 * 親眼看到 ✅/❌，而不是相信我們寫在頁面上的「已驗證」四個字。
 *
 * 兩個獨立的檢查：
 *
 *  A. **Seed commitment**（契約完全明定，無歧義）
 *       SHA256(server_seed) === server_seed_commit
 *     這證明：伺服器在對局開始前就把 seed 定死了，不能等看到你出剪刀才決定 seed。
 *
 *  B. **Transcript hash**（C01 §4.2.1 v1.2 已把編碼定死）
 *     後端的 `engine-canonical-v1` 是**權威**，前端**逐位元組重現它**，不得自創編碼：
 *
 *       bytes = server_seed(32 raw bytes)
 *             ‖ utf8("|" + match_id)
 *             ‖ for each round（round_no 由小到大）:
 *                   utf8("|" + round_no + "|" + seatActions + "|" + outcome)
 *       seatActions = 依 seat **數值**升冪，`<seat>:<action_key>`，以 "," 相連
 *       outcome     = winner 為 null → "draw"；否則 "win:<seat>"
 *       transcript_hash = "sha256:" + hex(SHA-256(bytes))
 *
 *     ★ 三個最容易踩錯、也最容易「兩邊各自測都會過」的地方：
 *       1. **seed 進雜湊的是原始 32 bytes，不是它的 hex 字串。**
 *       2. 分隔符全部是 `|`（回合之間沒有 `;`）、座位之間是 `,`、座位與動作之間是 `:`。
 *       3. `match_id` 走 **UTF-8** 位元組（非 ASCII 的 match_id 也要對得上）。
 *
 *     行為規則（C01 §4.2.1 規則 3）：
 *       * 若 replay 帶的 `transcript_encoding` 不是我們認得的版本 → 回報
 *         `unsupported_encoding` → UI 顯示「**無法驗證**」。
 *         **絕不**顯示「驗證失敗」—— 那是在指控伺服器作弊，兩者對使用者的意義完全不同。
 *       * 若真的對不上 → ❌ 並把我們算出來的字串攤開給玩家看，方便回報。
 *
 *     兩邊的一致性由**共用測試向量** `transcript-vectors.json` 釘住：
 *     後端的 `tests/core/test_fairness.py` 與本目錄的 `transcript.test.ts` 讀**同一個檔案**，
 *     所以任何一邊改了編碼、另一邊沒跟上，兩個 CI 都會紅。
 *
 * 什麼是這個協定**不能**保證的（誠實揭露，寫在 UI 上）：
 *   伺服器仍然可以在收到雙方動作之後才決定 server_seed 的用途。
 *   要完全排除，需要玩家端也做 commit-reveal（C01 §7 列為 v2）。
 */

import type { HistoryEntry, Seat } from '../api/types';

/** C01 §4.2.1 規則 2：後端定義的規範編碼識別字串。前端只認得這一個。 */
export const TRANSCRIPT_ENCODING = 'engine-canonical-v1' as const;

/**
 * @deprecated 舊名稱。v1.2 之前前端自創了一套叫 `"v1"` 的編碼，那套與後端**位元組不相容**，
 * 所以驗證永遠不可能通過。保留這個匯出只是為了不打斷還在 import 它的模組；
 * 它現在指向唯一合法的編碼。
 */
export const TRANSCRIPT_ENCODING_V1 = TRANSCRIPT_ENCODING;

export interface TranscriptRound {
  round_no: number;
  actions: Record<string, string>;
  winner?: Seat | null;
  draw?: boolean;
  /** 後端 replay 直接給的正規字串（`"win:0"` / `"draw"`）；有就優先用。 */
  outcome?: string;
}

export interface TranscriptInput {
  matchId: string;
  serverSeed: string;
  rounds: Array<HistoryEntry | TranscriptRound>;
}

export type VerifyStatus = 'ok' | 'mismatch' | 'missing_data' | 'unsupported_encoding' | 'error';

export interface VerifyResult {
  status: VerifyStatus;
  /** 我們算出來的 hex（無 `sha256:` 前綴）。 */
  computed: string | null;
  /** 伺服器宣稱的 hex（無前綴）。 */
  expected: string | null;
  /** transcript 驗證時的正規化字串，攤給玩家看。 */
  preimage?: string;
  message?: string;
}

// ---------------------------------------------------------------------------
// 雜湊工具
// ---------------------------------------------------------------------------

const encoder = new TextEncoder();

function subtle(): SubtleCrypto {
  const c = globalThis.crypto;
  if (!c || !c.subtle) {
    // Web Crypto 只在 secure context（https / localhost）可用。
    throw new Error('Web Crypto 不可用：請在 https 或 localhost 開啟本頁');
  }
  return c.subtle;
}

export function toHex(bytes: Uint8Array): string {
  let out = '';
  for (const b of bytes) out += b.toString(16).padStart(2, '0');
  return out;
}

export function fromHex(hex: string): Uint8Array {
  const clean = stripPrefix(hex);
  if (clean.length % 2 !== 0 || /[^0-9a-f]/i.test(clean)) throw new Error(`不是合法的 hex：${hex}`);
  const out = new Uint8Array(clean.length / 2);
  for (let i = 0; i < out.length; i += 1) out[i] = Number.parseInt(clean.slice(i * 2, i * 2 + 2), 16);
  return out;
}

/**
 * 契約裡的雜湊字串長成 `sha256:<hex>`（C01 §4.2.1）；解析時也接受裸 hex 與
 * **重複的前綴**（v1.2 之前的呼叫端會自己補一個 `sha256:`）。
 * 產生器嚴格、驗證器寬鬆 —— 舊資料今天也必須驗得過。
 */
export function stripPrefix(value: string): string {
  return value.trim().toLowerCase().replace(/^(?:sha256:)+/, '');
}

/** 產生線路格式（C01 §4.2.1）：`sha256:<64 位小寫 hex>`。 */
export function toWireHash(hex: string): string {
  return `sha256:${stripPrefix(hex)}`;
}

export async function sha256Hex(data: Uint8Array | string): Promise<string> {
  const bytes = typeof data === 'string' ? encoder.encode(data) : data;
  // BufferSource 需要真正的 ArrayBuffer view
  const digest = await subtle().digest('SHA-256', bytes as unknown as BufferSource);
  return toHex(new Uint8Array(digest));
}

// ---------------------------------------------------------------------------
// A. Seed commitment
// ---------------------------------------------------------------------------

export async function verifySeedCommitment(serverSeed: string | null, commit: string | null): Promise<VerifyResult> {
  if (!serverSeed || !commit) {
    return { status: 'missing_data', computed: null, expected: commit ? stripPrefix(commit) : null };
  }
  try {
    // `server_seed` 是 32 bytes 的十六進位原文（C01 §4.1）。
    // 若後端送的不是 hex，就退而求其次對 UTF-8 位元組做雜湊，並在訊息裡說明。
    let computed: string;
    let note: string | undefined;
    try {
      computed = await sha256Hex(fromHex(serverSeed));
    } catch {
      computed = await sha256Hex(serverSeed);
      note = 'server_seed 不是 hex，改以 UTF-8 位元組計算';
    }
    const expected = stripPrefix(commit);
    return {
      status: computed === expected ? 'ok' : 'mismatch',
      computed,
      expected,
      ...(note ? { message: note } : {}),
    };
  } catch (e) {
    return { status: 'error', computed: null, expected: stripPrefix(commit), message: String(e) };
  }
}

// ---------------------------------------------------------------------------
// B. Transcript hash
// ---------------------------------------------------------------------------

/** 回合的正規 outcome 字串：`"win:<seat>"` 或 `"draw"`（後端 `canonical_outcome()`）。 */
export function canonicalOutcome(round: HistoryEntry | TranscriptRound): string {
  const explicit = (round as TranscriptRound).outcome;
  if (typeof explicit === 'string' && explicit) return explicit;
  const winner = round.winner;
  if ((round as TranscriptRound).draw === true || winner === null || winner === undefined) return 'draw';
  return `win:${winner}`;
}

/** 單一回合的正規片段：`"|<round_no>|<seat>:<action>,<seat>:<action>|<outcome>"`。 */
function canonicalRound(round: HistoryEntry | TranscriptRound): string {
  const seatActions = Object.entries(round.actions)
    .map(([seat, action]) => [Number(seat), action] as [Seat, string])
    .filter(([seat]) => Number.isFinite(seat))
    // ★ 依 seat 的**數值**排序。用字串排序的話 seat 10 會排在 seat 2 前面，
    //   而後端 `sorted(..., key=int)` 不會 —— 這種分岔只在 3 個座位以上才看得到。
    .sort((a, b) => a[0] - b[0])
    .map(([seat, action]) => `${seat}:${action}`)
    .join(',');
  return `|${round.round_no}|${seatActions}|${canonicalOutcome(round)}`;
}

/**
 * `engine-canonical-v1` **實際被雜湊的位元組**（見檔頭 B）。
 *
 * ★ `serverSeed` 在這裡被還原成**原始 32 bytes**再進雜湊，不是把 hex 字串丟進去。
 *   這是前後端最容易分岔的一點，也是 v1.2 之前前端永遠算不出正確值的原因之一。
 */
export function buildTranscriptBytes(input: TranscriptInput): Uint8Array {
  const seed = fromHex(input.serverSeed);
  const tail = encoder.encode(
    `|${input.matchId}` +
      [...input.rounds]
        .sort((a, b) => a.round_no - b.round_no)
        .map(canonicalRound)
        .join(''),
  );
  const out = new Uint8Array(seed.length + tail.length);
  out.set(seed, 0);
  out.set(tail, seed.length);
  return out;
}

/**
 * 給人看的 preimage（UI 會把它攤開讓玩家自己核對）。
 *
 * ⚠️ 前面那段 seed 這裡印的是 **hex**，但真正進雜湊的是它的原始位元組
 * （見 `buildTranscriptBytes`）。要自己重算的人：
 *
 * ```bash
 * printf '%s' '<這串的 hex 版>' | xxd -r -p | sha256sum
 * ```
 */
export function buildTranscriptPreimage(input: TranscriptInput): string {
  return (
    `${stripPrefix(input.serverSeed)}|${input.matchId}` +
    [...input.rounds]
      .sort((a, b) => a.round_no - b.round_no)
      .map(canonicalRound)
      .join('')
  );
}

export async function verifyTranscriptHash(
  input: TranscriptInput,
  expectedHash: string | null,
  encoding: string | undefined = TRANSCRIPT_ENCODING,
): Promise<VerifyResult> {
  if (!expectedHash || !input.serverSeed || input.rounds.length === 0) {
    return { status: 'missing_data', computed: null, expected: expectedHash ? stripPrefix(expectedHash) : null };
  }
  if (encoding !== TRANSCRIPT_ENCODING) {
    // ★ 不認得的編碼 = **無法驗證**，不是驗證失敗（C01 §4.2.1 規則 3）。
    //   訊息開頭刻意就是「無法驗證」四個字：`FairnessPanel` 會把 `message` 直接
    //   顯示在狀態標籤後面，所以這裡的措辭就是使用者真正看到的字。
    //   「無法驗證」= 我算不出來；「驗證失敗」= 我算出來了而且對不上（等於在指控
    //   伺服器作弊）。把前者說成後者是一個獨立的正確性 bug。
    return {
      status: 'unsupported_encoding',
      computed: null,
      expected: stripPrefix(expectedHash),
      message: `無法驗證：後端宣告的 transcript 編碼是 "${encoding}"，本前端只實作 "${TRANSCRIPT_ENCODING}"`,
    };
  }
  try {
    const computed = await sha256Hex(buildTranscriptBytes(input));
    const expected = stripPrefix(expectedHash);
    return {
      status: computed === expected ? 'ok' : 'mismatch',
      computed,
      expected,
      preimage: buildTranscriptPreimage(input),
    };
  } catch (e) {
    return { status: 'error', computed: null, expected: stripPrefix(expectedHash), message: String(e) };
  }
}

export interface FullVerification {
  commitment: VerifyResult;
  transcript: VerifyResult;
  /** 兩項都 ok 才算全綠。 */
  allOk: boolean;
}

export async function verifyEverything(args: {
  matchId: string;
  serverSeed: string | null;
  serverSeedCommit: string | null;
  transcriptHash: string | null;
  rounds: Array<HistoryEntry | TranscriptRound>;
  encoding?: string;
}): Promise<FullVerification> {
  const commitment = await verifySeedCommitment(args.serverSeed, args.serverSeedCommit);
  const transcript = await verifyTranscriptHash(
    { matchId: args.matchId, serverSeed: args.serverSeed ?? '', rounds: args.rounds },
    args.transcriptHash,
    args.encoding,
  );
  return { commitment, transcript, allOk: commitment.status === 'ok' && transcript.status === 'ok' };
}
